import { NOTIFICATION_TYPE_ERROR } from 'ui-components/services/notification-service';
import { NotificationHubService } from '../../../../store/notification-hub/notification-hub-service';
import { NotificationType } from '../../../../store/notification-hub/notification-hub.types';

import messages from './messages';

const MAX_WAREHOUSES = 3;

const FORECASTING_CSV_COLUMNS = [
  { key: 'sku', messageKey: 'skuHeader' },
  { key: 'productName', messageKey: 'productNameHeader' },
  { key: 'currentOnHand', messageKey: 'currentOnHandHeader' },
  { key: 'reorderThreshold', messageKey: 'reorderThresholdHeader' },
  { key: 'avgOrders', messageKey: 'avgOrdersHeader' },
  { key: 'suggestedReorder', messageKey: 'suggestedReorderHeader' },
  { key: 'quantity', messageKey: 'quantityHeader' },
  { key: 'inventoryWarehouseId', messageKey: 'warehouseIdHeader' },
  { key: 'warehouseLocation', messageKey: 'warehouseLocationHeader' },
  { key: 'warehouseName1', messageKey: 'warehouse1Header' },
  { key: 'warehouseName2', messageKey: 'warehouse2Header' },
  { key: 'warehouseName3', messageKey: 'warehouse3Header' },
  { key: 'total', messageKey: 'totalHeader' },
  { key: 'available', messageKey: 'availableHeader' },
  { key: 'allocated', messageKey: 'allocatedHeader' },
  { key: 'productType', messageKey: 'productTypeHeader' },
  {
    key: 'totalAvailableAllWarehouses',
    messageKey: 'totalAvailableAllWarehousesHeader',
  },
];

/**
 * Escapes a CSV value by wrapping in quotes and escaping existing quotes
 * @param {string} value - Value to escape
 * @returns {string} - Escaped value
 */
const escapeCSVValue = (value) => {
  if (typeof value === 'string' && (value.includes(',') || value.includes('"') || value.includes('\n'))) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
};

/**
 * Converts an array of objects to a CSV string
 * @param {Array<object>} data - Array of objects to convert to CSV
 * @param {Array<string>} headers - Array of header names for the CSV
 * @param {Array<string>} keys - Array of object keys corresponding to the headers
 * @returns {string} - CSV string
 */
export const convertToCSV = (data, headers, keys) => {
  if (!data?.length) {
    return '';
  }

  if (!headers || !keys || headers.length !== keys.length) {
    throw new Error('Headers and keys arrays must have the same length');
  }

  const headerRow = headers.map(escapeCSVValue).join(',');

  const rows = data.map((item) => {
    return keys
      .map((key) => {
        const value = key.split('.').reduce((obj, k) => (obj && obj[k] !== undefined ? obj[k] : ''), item);
        const finalValue = value !== undefined && value !== null ? value : '';
        return escapeCSVValue(finalValue);
      })
      .join(',');
  });

  return [headerRow, ...rows].join('\n');
};

/**
 * Triggers a download of a CSV file
 * @param {string} csvContent - CSV content to download
 * @param {string} fileName - Name of the file to download
 * @param {object} intl - Internationalization object for formatting messages
 */
export const downloadCSV = (csvContent, fileName, intl) => {
  try {
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute('download', fileName);
    link.style.visibility = 'hidden';

    document.body.appendChild(link);
    link.click();

    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  } catch {
    NotificationHubService.showNotification(
      {
        notificationType: NotificationType.FORECASTING_EXPORT_FAILED,
        defaultTitle: messages.csvDownloadError.defaultMessage,
        defaultDescription: messages.csvDownloadError.defaultMessage,
      },
      {
        title: intl.formatMessage(messages.csvDownloadError),
        notificationType: NOTIFICATION_TYPE_ERROR,
      },
    );
    throw new Error('CSV download failed');
  }
};

/**
 * Prepares forecasting data for CSV export
 * @param {Array<object>} data - Forecasting data
 * @param {Array<object>} rawEntities - Raw entity data
 * @param {object} intl - Internationalization object for formatting messages
 * @returns {object} - Object with headers, keys, and prepared data
 */
export const prepareCSVData = (data, rawEntities, intl) => {
  const entityMap = rawEntities.reduce((acc, entity) => {
    return { ...acc, [entity.id]: entity };
  }, {});

  const preparedData = data.map((item) => {
    const rawEntity = entityMap[item.id] ?? {};

    const warehouses = rawEntity.productWarehouses ?? [];
    const warehouseNames = warehouses.map((w) => w.warehouseName ?? '').slice(0, MAX_WAREHOUSES);

    while (warehouseNames.length < MAX_WAREHOUSES) {
      warehouseNames.push('');
    }

    const locationParts = [rawEntity.area ?? '', rawEntity.unit ?? '', rawEntity.shelf ?? '', rawEntity.bin ?? ''].filter(Boolean);
    const locationString = locationParts.join('-');

    return {
      ...item,
      warehouseName1: warehouseNames[0],
      warehouseName2: warehouseNames[1],
      warehouseName3: warehouseNames[2],
      warehouseLocation: locationString,
      inventoryWarehouseId: rawEntity.inventoryWarehouseId ?? '',
      inventoryLocationId: rawEntity.inventoryLocationId ?? '',
      total: rawEntity.total ?? 0,
      available: rawEntity.available ?? 0,
      allocated: rawEntity.allocated ?? 0,
      productType: rawEntity.productType ?? '',
      totalAvailableAllWarehouses: rawEntity.totalAvailableAllWarehouses ?? 0,
    };
  });

  const headers = FORECASTING_CSV_COLUMNS.map((column) => intl.formatMessage(messages[column.messageKey]));
  const keys = FORECASTING_CSV_COLUMNS.map((column) => column.key);

  return {
    headers,
    keys,
    data: preparedData,
  };
};
